/**
 * What one import's files come to, for the review and for the banner that
 * offers it (Phase 3f).
 *
 * Worked out from the documents list the screens already hold, rather than
 * asked of the server. Every fact here is on each document — its question,
 * its status, its card, its family — so a second endpoint would be a second
 * place for the same answer to drift.
 */
import type { DocumentLink, ProjectDocument, ProjectImport } from "@gb/contracts";

export interface ReviewFacts {
  /** Every stored file that arrived in this import. */
  files: ProjectDocument[];
  /** The model was not sure where it sits among its versions, and asked. */
  asking: ProjectDocument[];
  /** Flagged by the check and hidden from every answer until someone keeps it. */
  hidden: ProjectDocument[];
  /**
   * The check would have hidden it, but asked a person instead (2026-09-26).
   * Still used in answers until someone hides it or keeps it.
   */
  unsure: ProjectDocument[];
  /** Flagged, then kept by a person. Listed so a keep does not vanish from view. */
  kept: ProjectDocument[];
  /** Nothing has said what it is: no card was written. */
  unread: ProjectDocument[];
  /** Not simply current on its own: a copy, an older version, another format. */
  placed: ProjectDocument[];
}

export function reviewFacts(
  importId: string,
  documents: readonly ProjectDocument[],
): ReviewFacts {
  const files = documents.filter((document) => document.importId === importId);
  // A copy is its original. It is never read, never carded on its own and
  // never flagged on its own, so it is only ever counted as a copy.
  const own = files.filter((document) => document.family.role !== "identical-copy");
  const visible = own.filter((document) => !document.status.hidden);

  return {
    files,
    asking: visible.filter((document) => document.question !== null),
    hidden: own.filter((document) => document.status.hidden),
    unsure: visible.filter((document) => document.status.question !== null),
    kept: visible.filter(
      (document) =>
        document.status.status === "this-project" && document.status.madeBy === "person",
    ),
    // A hidden file is listed under its flag, and a file the check asked about
    // under its question. Listing it here as well would ask a person to look
    // at the same file twice for two reasons.
    unread: visible.filter((document) => !document.card.written && document.status.question === null),
    placed: files.filter(
      (document) =>
        !document.status.hidden &&
        (document.family.role !== "current" || document.family.id !== null),
    ),
  };
}

/**
 * How many questions of an import still wait for a person: a placement, a
 * link and a file the check asked about (2026-09-26, tracker HID-04). While
 * one waits, the banner keeps the import's line, even once it is marked
 * reviewed. The flags are not counted: a hide the check was sure of is not a
 * question. The default the owner can change: this is the one place that says
 * which answers keep the line.
 */
export function questionsOpen(facts: Pick<ReviewFacts, "asking" | "unsure">, links: number): number {
  return facts.asking.length + links + facts.unsure.length;
}

/** "1 file", "26 files". */
export function files(n: number): string {
  return n === 1 ? "1 file" : `${n} files`;
}

/** "1 link", "15 links". */
export function links(n: number): string {
  return n === 1 ? "1 link" : `${n} links`;
}

/**
 * What is still open beside "Mark reviewed", one unit to a sentence
 * (2026-09-25, `document-ai/0017` §14): "28 files need a decision. 15 links
 * need an answer. 87 files are still flagged." It said "28 questions are still
 * open" while 15 links waited too, calling the files with a question all the
 * questions there were. Where each stays is said once. Null when nothing is
 * open.
 *
 * The files the check asked about are counted on their own since 2026-09-26:
 * they are not flagged, and they are still used in answers.
 *
 * Since 2026-09-26, later (HID-04), a question left open keeps the import's
 * line at the top of the Brain and the Ask tab, and this says so.
 */
export function stillOpen(open: {
  asking: number;
  links: number;
  hidden: number;
  unsure?: number;
}): string | null {
  const unsure = open.unsure ?? 0;
  const total = open.asking + open.links + open.hidden + unsure;
  if (total === 0) return null;
  const fileCount = open.asking + open.hidden + unsure;
  return [
    open.asking > 0 ? `${files(open.asking)} ${open.asking === 1 ? "needs" : "need"} a decision.` : null,
    open.links > 0 ? `${links(open.links)} ${open.links === 1 ? "needs" : "need"} an answer.` : null,
    unsure > 0 ? `${files(unsure)} ${unsure === 1 ? "waits" : "wait"} to be hidden or kept.` : null,
    open.hidden > 0 ? `${files(open.hidden)} ${open.hidden === 1 ? "is" : "are"} still flagged.` : null,
    `Marking it reviewed leaves ${total === 1 ? "it" : "them"} open.`,
    fileCount > 0 ? `${fileCount === 1 ? "The file stays" : "The files stay"} on the Brain’s shelves.` : null,
    open.links > 0
      ? open.links === 1
        ? "The link stays on its file’s page."
        : "The links stay on each file’s page."
      : null,
    open.asking + open.links + unsure > 0
      ? "The line at the top of the Brain and Ask stays until each question has an answer."
      : null,
  ]
    .filter((sentence): sentence is string => sentence !== null)
    .join(" ");
}

/**
 * What waits for a person across the whole project, for the one banner on the
 * Brain and the one review screen it opens (2026-10-03, the owner).
 *
 * Until then each import had its own line and its own review, and the check's
 * open questions had a second notice of their own. A project uploaded one file
 * at a time had one line per file, and the same file could be in both.
 */
export interface ProjectReviewFacts {
  /** Files the model asked about: where each sits among its versions. */
  asking: ProjectDocument[];
  /** Files the check would have hidden, and asked about instead. */
  unsure: ProjectDocument[];
  /** Files the check hid, in an import nobody has marked reviewed yet. */
  hidden: ProjectDocument[];
  /** Links waiting for an answer; empty while their list loads. */
  links: DocumentLink[];
  /** The imports nobody has marked reviewed yet, oldest first. */
  waiting: ProjectImport[];
  /**
   * The distinct files that wait for a person, for "X documents awaiting". A
   * file with two questions is one file. A link counts the file it is from.
   */
  awaiting: number;
}

export function projectReviewFacts(
  imports: readonly ProjectImport[],
  documents: readonly ProjectDocument[],
  askedLinks: readonly DocumentLink[],
): ProjectReviewFacts {
  const waiting = imports
    .filter((bundle) => bundle.status === "review")
    .sort((a, b) => a.openedAt.localeCompare(b.openedAt));
  const unreviewed = new Set(waiting.map((bundle) => bundle.id));
  // A copy is its original, and is never asked about on its own.
  const own = documents.filter((document) => document.family.role !== "identical-copy");
  const visible = own.filter((document) => !document.status.hidden);

  const asking = visible.filter((document) => document.question !== null);
  const unsure = visible.filter((document) => document.status.question !== null);
  // A hide the check was sure of is not a question. It is said only until a
  // person has looked at its import, as the import's own review said it.
  const hidden = own.filter(
    (document) => document.status.hidden && document.importId !== null && unreviewed.has(document.importId),
  );
  const ids = new Set([...asking, ...unsure, ...hidden].map((document) => document.id));
  for (const link of askedLinks) ids.add(link.from.id);

  return { asking, unsure, hidden, links: [...askedLinks], waiting, awaiting: ids.size };
}

/** "1 document", "15 documents". */
export function documentsWord(n: number): string {
  return n === 1 ? "1 document" : `${n} documents`;
}
